import { Button } from '@edsn/pie-components';
import PersonOffIcon from '@iconify/icons-material-symbols/block';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  getContactDetails,
  getGetContactDetailsIdQueryKey,
  useDeleteContactDetailsId,
  useGetCprManagementContacts,
} from '#pie/internal/api/pie-bff';
import { emptyFilterAtom } from '#pie/stores/searchOnlyFilter';

interface Props {
  userId: string;
  disabled?: boolean;
}

export const DeleteOrganisationUserModal = ({ userId, disabled }: Props) => {
  const [open, setOpen] = useState(false);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { t: tcom } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const navigate = useNavigate();
  const filters = useAtomValue(emptyFilterAtom);

  const { data: contactGroups } = useGetCprManagementContacts({
    pagination: { pageIndex: 0, pageSize: 100 },
    filter: filters,
  });

  const connectedContactGroups = contactGroups?.items
    .filter((ccg) => ccg.contactId === userId)
    .map((cg) => cg.contactGroup.name);

  const requiredContactGroups = contactGroups?.items.filter(
    (cg) => cg.contactGroup.isRequired,
  );

  const connectedRequiredContactGroups = requiredContactGroups
    ?.filter((rcg) => rcg.contactId === userId)
    .map((cg) => cg.contactGroup.name);

  const isUserConnectedToContactGroup = !!connectedContactGroups?.length;
  const isUserConnectedToRequiredContactGroup =
    !!connectedRequiredContactGroups?.length;

  const { mutate, isPending: isLoading } = useDeleteContactDetailsId({
    mutation: {
      onError: (error) => {
        addToast({
          message: tom('toast.error.message.user_cannot_be_deleted'),
          title: tom('toast.error.title.delete_user'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: tcom('common.deleted_user'),
          title: tom('toast.succes.title.user_deleted'),
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: getGetContactDetailsIdQueryKey(userId),
        });
        queryClient.invalidateQueries({ queryKey: [getContactDetails.name] });
        navigate('/beheer/gebruikersbeheer/');
      },
    },
  });

  const handleOnClick = () => {
    mutate({ id: userId });
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={tcom('common.delete_user')}
      >
        <Stack gap="md">
          {isUserConnectedToRequiredContactGroup ? (
            <Text>
              <Trans
                i18nKey="delete.user.message.contactgroup_required"
                t={tom}
                values={{ groups: connectedRequiredContactGroups?.join(', ') }}
                count={connectedRequiredContactGroups?.length}
              />
            </Text>
          ) : isUserConnectedToContactGroup ? (
            <Text>
              <Trans
                i18nKey="delete.user.message.contactgroup"
                t={tom}
                values={{ groups: connectedContactGroups?.join(', ') }}
                count={connectedContactGroups?.length}
              />
            </Text>
          ) : (
            tom('delete.user.message')
          )}
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => setOpen(false)}
            >
              {tcom('common.button.cancel')}
            </Button>
            <Button
              action="danger"
              emphasis="bold"
              onClick={handleOnClick}
              disabled={isUserConnectedToRequiredContactGroup}
            >
              {tcom('common.delete_user')}
            </Button>
          </div>
        </Stack>
      </Dialog>
      <Button
        action="tertiary"
        emphasis="subtle"
        isLoading={isLoading}
        disabled={disabled}
        iconStart={
          <Icon
            icon={PersonOffIcon}
            className="text-action-secondary-enabled flex items-center"
          />
        }
        onClick={() => setOpen(true)}
      >
        {tcom('common.delete_user')}
      </Button>
    </>
  );
};
